import React, { FC, useState, useEffect, useContext } from 'react'
import * as Sentry from '@sentry/react'
import { Link } from 'react-router-dom'
import { useApolloClient } from '@apollo/client'
import { Helmet } from 'react-helmet'
import { useParams, useHistory } from 'react-router'
import { ArrowLeftIcon, CheckIcon, XIcon } from '@heroicons/react/outline'
import useCart from '../../redux/shopping-cart/useCart'
import usePricing from '../../redux/pricing/usePricing'
import RenderSlate from '../Slate/RenderSlate'
import ROUTES from '../utils/routes'
import { courseFindMany } from '../../graphql/Courses/courseFindMany'
import { CourseType } from '../../types'
import { WidthController } from '../Grid'
import { Spinner, Button, Heading, VideoPlayer } from '../System'
import { useNotifications } from '../Notifications'
import { formatVideoLength } from '../utils/formatVideoLength'
import { getIconFromString } from '../System/IconPicker/getIconFromString'
import {
  getCourseUrl,
  getCourseVideoLength,
  getCourseLessonCount,
  ShareOptions
} from '../CourseLibraryView'
import { StyledCoursePreviewView, ModulePreviewSection } from '.'
import { CartPrice } from '../Cart'
import { userStore } from '../utils/UserContext'
import useSegment from '../utils/useSegment'

const CoursePreviewView: FC = () => {
  const { track } = useSegment()
  const { actions: { getPriceData } } = usePricing()
  const { actions: { idInCart, addToCart } } = useCart()

  const apollo = useApolloClient()
  const history = useHistory()
  const coursePrice = getPriceData('course')
  const { user } = useContext(userStore)
  const { addNotification } = useNotifications()
  const { courseSlug }: any = useParams()

  const [course, setCourse] = useState<CourseType>()

  const loadCourseBySlug = async () => {
    try {
      const { data } = await apollo.query({
        fetchPolicy: 'no-cache',
        query: courseFindMany,
        variables: { slug: courseSlug }
      })

      const course = data.courseFindMany.docs[0]

      setCourse(course)
      track('Viewed Course Preview Page', {
        id: course._id,
        title: course.title
      })
    }
    catch (err) {
      addNotification({ message: err.message, type: 'error', interval: 10000 })
      Sentry.captureException(err)
    }
  }

  const handleAddToCart = () => {
    if (!course) {
      return
    }

    if (!idInCart(course._id)) {
      return addToCart({ itemData: course, type: 'course' }, 'course-preview')
    }

    history.push('/checkout')
  }

  // Track when the user shares the course's URL
  const trackShare = (type: string) => {
    track('Shared Course', {
      id: course?._id,
      title: course?.title,
      share_type: type
    })
  }

  useEffect(() => {
    loadCourseBySlug()
  }, [courseSlug])

  useEffect(() => {
    if (course?.isOwned) {
      history.push(getCourseUrl(course, true))
    }
  }, [course])

  if (!course) {
    return <Spinner isBlock />
  }

  const inCart = idInCart(course._id)

  return (
    <StyledCoursePreviewView>
      <Helmet>
        <title>{course.title}</title>
        <meta property="og:title" content={course.title} />
        <meta property="og:type" content="website" />
        {course.coverImage && <meta property="og:image" content={course.coverImage.fileHttpLink} />}
        <meta name="twitter:card" content="summary_large_image" />
        <meta property="og:description" content={course.previewDescription} />
        <meta property="og:site_name" content="Social Curator" />
      </Helmet>
      <WidthController maxWidth={980} noPadding>
        {user && (
          <div className="course-top-bar">
            <Link to={ROUTES.dashboard.courses}>
              <Button theme="ghost" type="small" iconPos="left">
                <ArrowLeftIcon />
                Back To Course Library
              </Button>
            </Link>
          </div>
        )}
        {course.teaser && course.teaser.svid && <VideoPlayer videoId={course.teaser.svid} />}
        <div className="course-details">
          <Heading>{course.title}</Heading>
          <span>
            Hosted by <mark>Jasmine Star</mark>
            <span className="bullet" />
            {getCourseLessonCount(course)} Lessons
            <span className="bullet" />
            {formatVideoLength(getCourseVideoLength(course))}
          </span>
        </div>
        <div className="course-description">
          <RenderSlate html={course.description} />
        </div>
        <div className="course-actions">
          <div className="course-cart-info">
            {coursePrice && <CartPrice item={coursePrice} />}
            {!user && (
              <Link to={ROUTES.user.signupCourse}>
                <Button>Purchase Course</Button>
              </Link>
            )}
            {user && (
              <Button
                onClick={() => handleAddToCart()}
                isActive={inCart}
                iconPos="left"
                theme={inCart ? 'outline' : 'solid'}
              >
                {inCart ? (
                  <>
                    <CheckIcon /> In Cart
                  </>
                ) : (
                  'Add to Cart'
                )}
              </Button>
            )}
          </div>
          <ShareOptions
            shareURL={`${window.location}/${course.slug}/preview`}
            onShare={(type) => trackShare(type)}
          />
        </div>
        {(course.offeringsDescription.length > 0 || course.includesDescription.length > 0) && (
          <div className="course-section course-breakdown">
            {course.offeringsDescription.length > 0 && (
              <div className="course-bullets-container">
                <Heading type="h2">WHAT YOU'LL LEARN</Heading>
                <ul className="course-bullets">
                  {course.offeringsDescription.map((bullet, index) => {
                    return (
                      <li key={index}>
                        <CheckIcon />
                        {bullet}
                      </li>
                    )
                  })}
                </ul>
              </div>
            )}
            {course.includesDescription.length > 0 && (
              <div className="course-bullets-container">
                <Heading type="h2">THIS COURSE INCLUDES</Heading>
                <ul className="course-bullets">
                  {course.includesDescription.map((bullet, index) => {
                    return (
                      <li key={index}>
                        {getIconFromString(bullet.icon) || <XIcon />}
                        {bullet.label}
                      </li>
                    )
                  })}
                </ul>
              </div>
            )}
          </div>
        )}
        <div className="course-section">
          <Heading type="h2">LESSONS IN THIS COURSE</Heading>
          {course.modules &&
            course.modules.map((module, index) => {
              return <ModulePreviewSection module={module} key={index} />
            })}
        </div>
        <div className="course-floating-bar">
          {coursePrice && <CartPrice item={coursePrice} />}
          {!user && (
            <Link to={ROUTES.user.signupCourse}>
              <Button>Purchase Course</Button>
            </Link>
          )}
          {user && (
            <Button
              onClick={() => handleAddToCart()}
              isActive={inCart}
              iconPos="left"
              theme={inCart ? 'outline' : 'solid'}
            >
              {inCart ? (
                <>
                  <CheckIcon /> In Cart
                </>
              ) : (
                'Add to Cart'
              )}
            </Button>
          )}
        </div>
      </WidthController>
    </StyledCoursePreviewView>
  )
}

export default CoursePreviewView
